import {
	CUSTOM_FIELD_KEYS,
	ExtensionStayEnum,
	ExtensionStayLabel,
	PageTypeEnum,
	ResidenceEnum,
	ResidenceLabel,
	TEMPLATE_ASSIGNMENT_FIELDS,
	TEMPLATE_PUBLIC_ASSIGNMENT_FIELDS,
	VisaTimeEnum,
	VisaTimeLabel,
} from '@/constant';
import {
	GetWorkerAssignmentApplicantListResponse,
	GetWorkerAssignmentDetailResponse,
	GetWorkerAssignmentListResponse,
	IApplicantListItem,
	IFranchiseListItem,
	IRecommenderListItem,
	TUploadExcelAssignmentFailedItem,
	TUploadExcelAssignmentResponse,
	TUploadExcelPublicAssignmentFailedItem,
	TUploadExcelPublicAssignmentResponse,
	WorkerLodgingListItem,
} from '@/services';

import {
	convertExpirationDate,
	filterByPrefixKeyWithFormat,
	formatCustomFieldValueToExcel,
	formatDate,
	generateErrorMessagesUploadExcel,
	getDiffExpirationDateString,
	insertObjectIfV2,
	map,
} from 'utils';

import { isNotEmpty } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { formatWorkerApplicantListItem } from '../worker-applicant';
import { formatWorkerFranchiseListItem } from '../worker-franchise';
import { formatWorkerLodgingListDetail } from '../worker-lodging';
import { formatWorkerRecommenderListItem } from '../worker-recommender';

export const formatWorkerAssignmentDetail = (
	input: GetWorkerAssignmentDetailResponse
) => {
	return {
		...input,
		registDt: formatDate(input.registDt),
		updateDt: formatDate(input.updateDt),
	};
};

export const formatWorkerAssignmentListItem = (
	input: GetWorkerAssignmentListResponse['content'][number]
) => {
	let _input = {
		...input,
	};

	let newFranchiseFormat = filterByPrefixKeyWithFormat<IFranchiseListItem>(
		_input,
		PageTypeEnum.FRANCHISE
	);
	if (isNotEmpty(newFranchiseFormat)) {
		newFranchiseFormat = formatWorkerFranchiseListItem(newFranchiseFormat);
	}

	let newApplicantFormat = filterByPrefixKeyWithFormat<IApplicantListItem>(
		_input,
		PageTypeEnum.APPLICANT
	);
	if (isNotEmpty(newApplicantFormat)) {
		newApplicantFormat = formatWorkerApplicantListItem(newApplicantFormat);
	}

	let newRecomenderFormat = filterByPrefixKeyWithFormat<IRecommenderListItem>(
		_input,
		PageTypeEnum.RECOMMENDER
	);
	if (isNotEmpty(newRecomenderFormat)) {
		newRecomenderFormat = formatWorkerRecommenderListItem(newRecomenderFormat);
	}

	let newLodgingFormat = filterByPrefixKeyWithFormat<WorkerLodgingListItem>(
		_input,
		PageTypeEnum.LODGING
	);
	if (isNotEmpty(newLodgingFormat)) {
		newLodgingFormat = formatWorkerLodgingListDetail(newLodgingFormat);
	}

	const formatValues = {
		...newFranchiseFormat,
		...newApplicantFormat,
		...newRecomenderFormat,
		...newLodgingFormat,
	} as typeof _input;

	const idUnique = `af${_input?.assignmentFranchiseId}${_input?.applicantId ? 'a' + _input?.applicantId : ''}`;

	return {
		..._input,
		...insertObjectIfV2(isNotEmpty(formatValues), {
			...formatValues,
		}),
		idUnique,
		id: _input?.assignmentFranchiseId,
		assignmentFranchiseStartDate: formatDate(
			_input?.assignmentFranchiseStartDate
		),
		assignmentFranchiseEndDate: formatDate(_input?.assignmentFranchiseEndDate),
		assignmentFranchiseRegistDt: formatDate(
			_input?.assignmentFranchiseRegistDt
		),
		assignmentFranchiseUpdateDt: formatDate(
			_input?.assignmentFranchiseUpdateDt
		),
	};
};

export const formatWorkerAssignmentList = (
	input: GetWorkerAssignmentListResponse['content']
) => {
	return map(input, formatWorkerAssignmentListItem);
};

export const formatWorkerAssignmentExcelList = (
	input: GetWorkerAssignmentListResponse['content']
) => {
	return map(input, (item) => {
		return {
			...formatWorkerAssignmentListItem(item),
			franchiseDistrictId: item?.franchiseDistrictName,
			franchisePublicFranchiseId: item.franchisePublicFranchiseName,
			franchiseProductionScale: isNotNilOrEmpty(item?.franchiseProductionScale)
				? Number(item?.franchiseProductionScale)?.toLocaleString?.('ko-KR')
				: '',

			applicantStatusId: item?.applicantStatusName,
			applicantIssuanceStatusId: item?.applicantIssuanceStatusName,
			applicantDepartureDate: convertExpirationDate(
				item.applicantDepartureDate
			),
			applicantExpirationDate: convertExpirationDate(
				item.applicantExpirationDate
			),
			applicantPublicFranchiseId: item?.applicantPublicFranchiseName,

			// assignmentFranchisePeriod: showDataGridWithSymbol(
			// 	formatDate(item?.assignmentFranchiseStartDate),
			// 	formatDate(item?.assignmentFranchiseEndDate),
			// 	'~',
			// 	true
			// ),
			...formatCustomFieldValueToExcel(
				[...CUSTOM_FIELD_KEYS.applicant, ...CUSTOM_FIELD_KEYS.franchise].reduce(
					(acc, key) => ({
						...acc,
						[key]: item?.[key as keyof typeof item],
					}),
					{}
				)
			),
		};
	});
};

export const formatWorkerAssignmentApplicantDetail = (
	input: GetWorkerAssignmentApplicantListResponse['content'][number]
) => {
	return {
		...input,
		birthDate: formatDate(input?.birthDate),
		workStartDate: formatDate(input?.workStartDate),
		workEndDate: formatDate(input?.workEndDate),
		entryDate: formatDate(input?.entryDate),
		extensionStayDate: formatDate(input?.extensionStayDate),
		expirationDate: isNotNilOrEmpty(input?.expirationDate)
			? getDiffExpirationDateString(input?.expirationDate)
			: '',
		changeWorkDate: formatDate(input?.changeWorkDate),
		unauthorizedDepartureDate: formatDate(input?.unauthorizedDepartureDate),
		departureDate: isNotNilOrEmpty(input?.departureDate)
			? getDiffExpirationDateString(input?.departureDate)
			: '',
		registDt: formatDate(input.registDt),
		visaCode: ResidenceLabel[input.visaCode] as ResidenceEnum,
		visaTime: VisaTimeLabel[input.visaTime] as VisaTimeEnum,
		extensionPeriod: ExtensionStayLabel[
			input.extensionPeriod
		] as ExtensionStayEnum,
	};
};

export const formatWorkerAssignmentApplicantList = (
	input: GetWorkerAssignmentApplicantListResponse['content']
) => {
	return map(input, formatWorkerAssignmentApplicantDetail);
};

export const formatUploadExcelAssignmentFailedItem = (
	input: TUploadExcelAssignmentFailedItem,
	programName: string
) => {
	// for handle alert-63
	const districtIdField = [{ label: '', value: 'districtId' }];

	return {
		...input.data,
		programName,
		reasonFailure: generateErrorMessagesUploadExcel({
			response: { errorList: input.errorList },
			templateFields: [...TEMPLATE_ASSIGNMENT_FIELDS, ...districtIdField],
			pageType: PageTypeEnum.ASSIGNMENT,
		}),
	};
};

export const formatUploadExcelAssignmentFailedList = (
	input: TUploadExcelAssignmentResponse,
	programName: string
) => {
	return map(input, (item) =>
		formatUploadExcelAssignmentFailedItem(item, programName)
	);
};

export const formatUploadExcelPublicAssignmentFailedItem = (
	input: TUploadExcelPublicAssignmentFailedItem,
	programName: string
) => {
	return {
		...input.data,
		programName,
		reasonFailure: generateErrorMessagesUploadExcel({
			response: { errorList: input.errorList, data: input.data },
			templateFields: TEMPLATE_PUBLIC_ASSIGNMENT_FIELDS,
			pageType: PageTypeEnum.PUBLIC_ASSIGNMENT,
		}),
	};
};

export const formatUploadExcelPublicAssignmentFailedList = (
	input: TUploadExcelPublicAssignmentResponse,
	programName: string
) => {
	return map(input, (item) =>
		formatUploadExcelPublicAssignmentFailedItem(item, programName)
	);
};
